import {
  authGetAuthorisedProducts,
  authLoginCustomer,
  authLoginStaff,
  authLoginSuperUser,
  companyGetUserDetails,
  customerGetAccountSettings,
  customerGetContact,
  customerGetGuest,
  processingGetProcessingLevel,
  staffGetOrganisationCustomerId
} from '@/api'
import { LoginStatus, ProductType } from '@/enums'
import { useBasketStore } from '@/pinia/basket'
import { useBrandedStore } from '@/pinia/branded'
import { useCustomerStore } from '@/pinia/customer'
import { useStyleStore } from '@/pinia/style'
import { defineStore } from 'pinia'

function assertLoginSucceeded(response) {
  if (response?.loginStatus === LoginStatus.SUCCESS) return

  const error = new Error(response?.message)
  error.loginStatus = LoginStatus.FAILURE
  error.popUp = {
    title: 'Login failed',
    text: response?.message
  }
  throw error
}

export const useAuthStore = defineStore('auth', {
  persist: true,
  state: () => ({
    installation: undefined,
    companyName: undefined,
    id: undefined,
    firstName: undefined,
    lastName: undefined,
    phone: undefined,
    email: undefined,
    username: undefined,
    image: undefined,
    loggedIn: false,
    processingLevel: undefined,
    usingBrandedModeToken: false,
    isDealerNetworkMode: false,
    staffLogin: false,
    admin: false,
    superuser: false,
    isBranchManager: 0,
    branches: [],
    accountSettings: {},
    login_url: '/login',
    licenses: [],
    sessionExpiry: undefined,
    sessionRefreshAfter: undefined,
    lastClosedMaintenancePopUp: undefined,
    isCookiePopUpHidden: false,
    isQuotilaBannerHidden: false
  }),
  actions: {
    setAuth({ property, tokenExpiry, refreshAfter }) {
      this.installation = property
      this.setSessionExpiry({ expiresAt: tokenExpiry, refreshAfter })
    },
    setSessionExpiry({ expiresAt, refreshAfter }) {
      this.sessionExpiry = expiresAt
      this.sessionRefreshAfter = refreshAfter
    },
    setIsBranchManager(isBranchManager) {
      this.isBranchManager = isBranchManager
    },
    login(user) {
      this.id = user.id
      this.companyName = user.companyName
      this.firstName = user.firstName
      this.lastName = user.lastName
      this.mobile = user.mobile
      this.email = user.email
      this.username = user.username
      this.image = user.image
      this.staffLogin = user.staffLogin
      this.admin = user.admin
      if (user.superuser) {
        this.superuser = user.superuser
      }
    },
    setAccountSettings(accountSettings) {
      this.accountSettings = accountSettings
    },
    setBranches(branches) {
      this.branches = branches
    },
    setBrandedMode(mode) {
      this.usingBrandedModeToken = mode
    },
    setDealerNetworkMode(mode) {
      this.isDealerNetworkMode = mode
    },
    async setLicenses() {
      const { products } = await authGetAuthorisedProducts()
      this.licenses = products
    },
    async setProcessingLevel() {
      const response = await processingGetProcessingLevel()
      this.processingLevel = parseInt(response)
    },
    async setVendorLogin({ isDealerNetwork, property, tokenExpiry, refreshAfter }) {
      const brandedStore = useBrandedStore()
      const styleStore = useStyleStore()
      this.setAuth({ property, tokenExpiry, refreshAfter })
      await this.setProcessingLevel()
      this.setBrandedMode(true)
      this.setDealerNetworkMode(isDealerNetwork)
      brandedStore.setBrandedSettings(await customerGetGuest())
      styleStore.refresh()
      await this.setLicenses()
      this.loggedIn = true
    },
    async setLogin() {
      const response = await companyGetUserDetails()
      this.login({
        id: response.userId,
        companyName: response.companyName,
        firstName: response.firstName,
        lastName: response.lastName,
        image: response.userImage,
        email: response.email,
        staffLogin: response.isStaff,
        admin: response.isAdmin
      })
      this.loggedIn = true
    },
    async validateLogin(property) {
      if (!this.loggedIn) {
        return false
      }

      if (property === 'branded') {
        this.setBrandedMode(true)
      } else {
        this.setBrandedMode(false)
      }

      return true
    },
    async attemptPortalLogin({ password, username }) {
      const response = await authLoginCustomer(username, password)
      assertLoginSucceeded(response)

      this.setAuth({
        property: 'customer',
        tokenExpiry: response.tokenExpiry,
        refreshAfter: response.refreshAfter
      })

      window.logoutPush = false
      this.login({
        id: response.id,
        companyName: response.companyName,
        firstName: response.firstName,
        lastName: response.lastName,
        image: response.image,
        mobile: response.mobile,
        email: username,
        username,
        staffLogin: false,
        admin: response.admin
      })

      const basketStore = useBasketStore()
      const customerStore = useCustomerStore()
      const customer = await customerStore.customerGet()
      customerStore.setCustomer(customer)
      const styleStore = useStyleStore()
      styleStore.refresh()
      basketStore.setSalesSectorType(customer.customer?.salesSectorType)

      this.setAccountSettings(await customerGetAccountSettings())
      await this.setProcessingLevel()

      window.gtag('event', 'Login', {
        fabricator: styleStore.fabricator.name,
        company: response.companyName,
        installation: 'portal',
        email: username
      })
      await this.setLicenses()
      this.loggedIn = true
    },
    async attemptSuperUserLogin({ password, username, email, superUserOverride }) {
      const response = await authLoginSuperUser(username, password, email)
      assertLoginSucceeded(response)

      const property = window.VITE_APP_INSTALLATION_TYPE === 'business' ? 'staff' : 'customer'
      this.setAuth({ property, tokenExpiry: response.tokenExpiry, refreshAfter: response.refreshAfter })
      this.login({
        id: response.id,
        companyName: response.companyName,
        firstName: response.firstName,
        lastName: response.lastName,
        image: response.image,
        mobile: response.mobile,
        email: email,
        username,
        staffLogin: window.VITE_APP_INSTALLATION_TYPE === 'business',
        admin: response.admin,
        superuser: superUserOverride
      })

      const customerStore = useCustomerStore()
      if (window.VITE_APP_INSTALLATION_TYPE === 'business') {
        const customer = await customerStore.customerGet(await staffGetOrganisationCustomerId())
        customerStore.setCustomer(customer)
        const user = await customerGetContact(response.id)
        this.setBranches(user.branches)
      } else {
        const basketStore = useBasketStore()
        const customer = await customerStore.customerGet()
        customerStore.setCustomer(customer)
        basketStore.setSalesSectorType(customer.customer?.salesSectorType)
      }
      const styleStore = useStyleStore()
      styleStore.refresh()
      await this.setProcessingLevel()
      this.setAccountSettings(await customerGetAccountSettings())
      window.gtag('event', 'Login', {
        fabricator: styleStore.fabricator.name,
        company: response.companyName,
        installation: window.VITE_APP_INSTALLATION_TYPE,
        email: username
      })
      await this.setLicenses()
      this.loggedIn = true
    },
    async attemptBusinessLogin({ password, username }) {
      const response = await authLoginStaff(username, password)
      assertLoginSucceeded(response)

      window.logoutPush = false

      this.setAuth({
        property: 'staff',
        tokenExpiry: response.tokenExpiry,
        refreshAfter: response.refreshAfter
      })

      this.login({
        id: response.id,
        companyName: response.companyName,
        firstName: response.firstName,
        lastName: response.lastName,
        image: response.image,
        mobile: response.mobile,
        email: username,
        username,
        staffLogin: true,
        admin: response.admin
      })

      const user = await customerGetContact(response.id)
      this.setBranches(user.branches)

      const customerStore = useCustomerStore()
      const customer = await customerStore.customerGet(await staffGetOrganisationCustomerId())
      customerStore.setCustomer(customer)
      const styleStore = useStyleStore()
      styleStore.refresh()
      const isBranchManager = customerStore.customer.users.filter((usr) => usr.id === response.id)[0].branchCompanyId
      this.setIsBranchManager(isBranchManager)
      this.setAccountSettings(await customerGetAccountSettings())
      await this.setProcessingLevel()
      window.gtag('event', 'Login', {
        fabricator: styleStore.fabricator.name,
        company: response.companyName,
        installation: 'admin',
        email: username
      })
      await this.setLicenses()
      this.loggedIn = true
    }
  },
  getters: {
    user(state) {
      return {
        id: state.id,
        companyName: state.companyName,
        firstName: state.firstName,
        lastName: state.lastName,
        image: state.image,
        email: state.email,
        mobile: state.mobile,
        staffLogin: state.staffLogin
      }
    },
    isBusiness() {
      return window.VITE_APP_INSTALLATION_TYPE === 'business'
    },
    isBusinessUser(state) {
      return state.isBusiness && state.loggedIn
    },
    isPortal() {
      return window.VITE_APP_INSTALLATION_TYPE === 'portal'
    },
    isPortalUser(state) {
      return state.isPortal && state.loggedIn
    },
    isBrandedModeUser(state) {
      return state.isPortal && state.loggedIn && state.usingBrandedModeToken
    },
    isExtruder(state) {
      return state.licenses.some((x) => x.type === ProductType.EXTRUDER && !x.isExpress)
    },
    isExpress(state) {
      return state.licenses.some((x) => x.type === ProductType.EXTRUDER && x.isExpress)
    },
    isExtruderOrExpress(state) {
      return state.licenses.some((x) => x.type === ProductType.EXTRUDER)
    },
    isExtruderOrExpressCustomer(state) {
      return state.isExtruderOrExpress && state.isPortalUser
    },
    isPayment(state) {
      return state.licenses.some((x) => x.type === ProductType.PAYMENT_GATEWAY)
    }
  }
})
